import React, { useEffect, useState } from "react";
import { useAuth } from "../../../AuthProvider/AuthContext";
import { Eye, EyeOff, Phone, Mail, ArrowLeft } from "lucide-react";
import { PhoneInput } from "react-international-phone";
import logo from "../../../assets/logo.png";
import "react-international-phone/style.css";
import "./Signup.css";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
const OTP_RESEND_SECONDS = 60;

function normalizePhoneNumber(value) {
  const normalized = String(value || "").replace(/[\s()-]/g, "");
  return /^\+[1-9]\d{7,14}$/.test(normalized) ? normalized : null;
}

async function readResponse(response) {
  try {
    return await response.json();
  } catch {
    return {};
  }
}

export default function Signup({ onClose, onSwitchToLogin }) {
  const { login } = useAuth();

  // WhatsApp OTP is the default authentication method.
  const [mode, setMode] = useState("phone");

  // Legacy email/password signup fields.
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);

  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (resendCountdown <= 0) return undefined;

    const timer = setInterval(() => {
      setResendCountdown((current) => Math.max(0, current - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCountdown]);

  function switchMode(nextMode) {
    setMode(nextMode);
    setError("");
    setOtp("");
    setOtpSent(false);
    setResendCountdown(0);
  }

  function startResendCountdown() {
    setResendCountdown(OTP_RESEND_SECONDS);
  }

  async function handleEmailSignup() {
    setError("");

    const normalizedName = name.trim();
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedName) {
      setError("Full name is required for email signup.");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      setError("Enter a valid email address.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }

    const phoneValue = phone.trim();
const isCountryCodeOnly = /^\+\d{1,3}$/.test(
  phoneValue.replace(/[\s()-]/g, "")
);

const normalizedPhone =
  phoneValue && !isCountryCodeOnly
    ? normalizePhoneNumber(phoneValue)
    : null;

if (phoneValue && !isCountryCodeOnly && !normalizedPhone) {
  setError("Enter a valid phone number.");
  return;
}
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: normalizedName,
          email: normalizedEmail,
          phone: normalizedPhone || "",
          password,
        }),
      });

      const data = await readResponse(response);

      if (!response.ok || !data.token || !data.user) {
        throw new Error(data.error || "Signup failed.");
      }

      login(data.user, data.token);
      onClose?.();
    } catch (err) {
      setError(err.message || "Could not create your account.");
    } finally {
      setLoading(false);
    }
  }

  async function handleSendOtp() {
    setError("");

    const normalizedPhone = normalizePhoneNumber(phone);

    if (!normalizedPhone) {
      setError("Enter a valid mobile number with the correct country code.");
      return;
    }

    if (resendCountdown > 0) {
      setError(
        `Please wait ${resendCountdown} seconds before requesting another OTP.`
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/auth/whatsapp/send-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone: normalizedPhone }),
        }
      );

      const data = await readResponse(response);

      if (!response.ok) {
        throw new Error(
          data.error || data.message || "Could not send WhatsApp OTP."
        );
      }

      setOtp("");
setOtpSent(true);
      startResendCountdown();
      setError("");
    } catch (err) {
      setError(err.message || "Could not send WhatsApp OTP. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyOtp() {
    setError("");

    const normalizedPhone = normalizePhoneNumber(phone);
    const enteredOtp = otp.trim();

    if (!normalizedPhone) {
      setError("Enter a valid mobile number.");
      return;
    }

    if (!otpSent) {
      setError("Request an OTP before continuing.");
      return;
    }

    if (!/^\d{6}$/.test(enteredOtp)) {
      setError("Enter the six-digit OTP sent to your WhatsApp.");
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${API_BASE}/api/auth/whatsapp/verify-otp`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone: normalizedPhone,
            otp: enteredOtp,
          }),
        }
      );

      const data = await readResponse(response);
      const appToken = data.token || data.accessToken;

      if (!response.ok || !appToken || !data.user) {
        throw new Error(
          data.error || data.message || "WhatsApp OTP verification failed."
        );
      }

      // The backend handles existing-user login and new-user creation.
      login(data.user, appToken);
      onClose?.();
    } catch (err) {
      setError(err.message || "Could not verify your WhatsApp OTP.");
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key !== "Enter" || loading) return;

    if (mode === "email") {
      handleEmailSignup();
    } else if (otpSent) {
      handleVerifyOtp();
    } else {
      handleSendOtp();
    }
  }

  return (
    <div className="nb-overlay" onClick={onClose}>
  <div
    className="nb-modal"
    onClick={(event) => event.stopPropagation()}
    role="dialog"
    aria-modal="true"
    aria-labelledby="signup-title"
  >
        <button
          type="button"
          className="nb-modal__close"
          onClick={onClose}
          aria-label="Close signup"
        >
          ×
        </button>



{/* Right signup panel */}
<section className="nb-signup-panel">
  <div className="nb-signup-panel__inner">

<div className="nb-signup-brand">
  <img
    src={logo}
    alt="MakeBeeTrip"
    className="nb-signup-brand__image"
  />
</div>

        <h2 id="signup-title" className="nb-modal__title">
          Create your account
        </h2>

        <p className="nb-modal__sub">
          Join millions of happy travellers
        </p>

        <div className="nb-login-mode-switch" role="tablist" aria-label="Signup method">
  <button
    type="button"
    role="tab"
    aria-selected={mode === "phone"}
    className={`nb-login-mode-button ${mode === "phone" ? "active" : ""}`}
    onClick={() => switchMode("phone")}
  >
    <Phone size={17} />
    <span>Mobile</span>
  </button>

  <button
    type="button"
    role="tab"
    aria-selected={mode === "email"}
    className={`nb-login-mode-button ${mode === "email" ? "active" : ""}`}
    onClick={() => switchMode("email")}
  >
    <Mail size={17} />
    <span>Email</span>
  </button>
</div>

        {error && (
          <p className="nb-modal__error" role="alert">
            {error}
          </p>
        )}

        

        {mode === "phone" ? (
          <>
            <div className="nb-field nb-phone-field">
              <label htmlFor="signup-phone">Mobile number</label>

              <PhoneInput
  defaultCountry="in"
  value={phone}
  disableDialCodeAndPrefix={true}
                onChange={(value) => {
  // PhoneInput maintains the selected country's dial code internally.
  setPhone(value);
  setOtp("");
  setOtpSent(false);
  setResendCountdown(0);
  setError("");
}}
                preferredCountries={["in", "us", "gb", "ae", "au", "sg"]}
                inputProps={{
                  id: "signup-phone",
                  name: "phone",
                  autoComplete: "tel",
                  placeholder: "Enter mobile number",
                  disabled: loading,
                  onKeyDown: handleKeyDown,
                  "aria-label": "Mobile number with country code",
                }}
                style={{ width: "100%" }}
                inputStyle={{
                  width: "100%",
                  minWidth: 0,
                  height: "46px",
                  fontSize: "14px",
                  color: "#0d1b3e",
                  background: "#ffffff",
                  border: "1px solid rgba(13,27,62,0.16)",
                  borderRadius: "0 8px 8px 0",
                  outline: "none",
                  boxSizing: "border-box",
                }}
                countrySelectorStyleProps={{
                  buttonStyle: {
                    width: "68px",
                    minWidth: "68px",
                    height: "46px",
                    padding: "0 7px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    background: "#f7f9fc",
                    border: "1px solid rgba(13,27,62,0.2)",
                    borderRight: "none",
                    borderRadius: "8px 0 0 8px",
                    cursor: "pointer",
                    boxSizing: "border-box",
                  },
                }}
              />
            </div>

            <p className="nb-modal__sub">
              Sign up or log in with your phone number.
            </p>

            {!otpSent ? (
              <button
                type="button"
                className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? "Sending OTP…" : "Send WhatsApp OTP"}
              </button>
            ) : (
              <>
                <p className="nb-modal__sub">
                  Enter the six-digit code sent to WhatsApp at{" "}
                  <strong>{normalizePhoneNumber(phone) || phone}</strong>.
                </p>

                <div className="nb-field">
                  <label htmlFor="signup-otp">Verification code</label>

                  <input
                    id="signup-otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="Enter 6-digit OTP"
                    maxLength={6}
                    value={otp}
                    onChange={(event) => {
                      setOtp(
                        event.target.value.replace(/\D/g, "").slice(0, 6)
                      );
                      setError("");
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                  />
                </div>

                <button
                  type="button"
                  className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"
                  onClick={handleVerifyOtp}
                  disabled={loading || otp.length !== 6}
                >
                  {loading ? "Verifying…" : "Verify & Create Account"}
                </button>

                <button
                  type="button"
                  className="nb-login-resend"
                  onClick={handleSendOtp}
                  disabled={loading || resendCountdown > 0}
                >
                  {resendCountdown > 0
                    ? `Resend OTP in ${resendCountdown}s`
                    : "Resend WhatsApp OTP"}
                </button>

                <button
                  type="button"
                  className="nb-login-resend"
                  onClick={() => {
                    setOtpSent(false);
                    setOtp("");
                    setResendCountdown(0);
                    setError("");
                  }}
                  disabled={loading}
                >
                  <ArrowLeft size={15} />
                  Change phone number
                </button>
              </>
            )}
          </>
        ) : (
          <>
          <div className="nb-modal__social">
  <button
    type="button"
    className="nb-social-btn"
    onClick={() => {
      window.location.href = `${API_BASE}/api/auth/google`;
    }}
  >
    <GoogleIcon />
    Continue with Google
  </button>
</div>

<div className="nb-modal__divider">
  <span>or</span>
</div>
            <div className="nb-field">
              <label htmlFor="signup-name">Full name</label>
              <input
                id="signup-name"
                type="text"
                placeholder="Your full name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="name"
                disabled={loading}
              />
            </div>

            <div className="nb-field">
              <label htmlFor="signup-email">Email address</label>
              <input
                id="signup-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                onKeyDown={handleKeyDown}
                autoComplete="email"
                disabled={loading}
              />
            </div>

            <div className="nb-field">
  <label htmlFor="signup-phone-email">
    Phone number{" "}
    <span
      style={{
        color: "var(--ink-4, #aaa)",
        fontWeight: 400,
      }}
    >
      (optional)
    </span>
  </label>

  <input
    id="signup-phone-email"
    type="tel"
    placeholder="+91 98765 43210"
    value={phone}
    onChange={(event) => setPhone(event.target.value)}
    onKeyDown={handleKeyDown}
    autoComplete="tel"
    disabled={loading}
  />
</div>

            <div className="nb-field">
              <label htmlFor="signup-password">Password</label>

              <div className="password-input-wrap">
                <input
                  id="signup-password"
                  type={showPass ? "text" : "password"}
                  placeholder="Minimum 8 characters"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  onKeyDown={handleKeyDown}
                  autoComplete="new-password"
                  disabled={loading}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPass((current) => !current)}
                  aria-label={showPass ? "Hide password" : "Show password"}
                >
                  {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="button"
              className="nb-btn nb-btn--primary nb-btn--full nb-modal__submit"
              onClick={handleEmailSignup}
              disabled={loading}
            >
              {loading ? "Creating account…" : "Create account"}
            </button>
          </>
        )}

        <p className="nb-modal__footer">
          Already have an account?{" "}
          <button
            type="button"
            className="nb-login-signup-link"
            onClick={() => {
              onClose?.();
              onSwitchToLogin?.();
            }}
          >
            Log in
          </button>
        </p>
  </div>
</section>
</div>
</div>
);
}

function GoogleIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}